// Call History tab — chronological list of this lead's calls from the lead
// payload's `calls` array (tenant-scoped upstream). Recorded calls get an
// inline player (streamed via the /api/recordings/:callId proxy) plus their
// stored AI call summary, so playback + summary sit UNDER the call, Ringy-style.

import React from 'react';
import { PhoneIncoming, PhoneOutgoing, Sparkles } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../../lib/api.js';
import { openRecordingStream, useRecordingStreamUrl } from '../../../lib/recordings.js';
import { fmtDate, fmtFromNow, fmtPhone } from '../../../lib/format.js';

/** @param {number} seconds @returns {string} e.g. "2m 14s" or "45s". */
export function dur(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  const m = Math.floor(s / 60);
  return m ? `${m}m ${s % 60}s` : `${s}s`;
}

// Mints a short-lived recording-scoped stream URL (via the /api/recordings
// proxy) and renders a native <audio> element. Falls back to a new-tab link
// while the token mints / if playback fails.
function RecordingPlayer({ callId }) {
  const url = useRecordingStreamUrl(callId);
  if (!url) {
    return (
      <button
        type="button"
        onClick={async () => {
          const u = await openRecordingStream(callId);
          if (u) window.open(u, '_blank', 'noopener,noreferrer');
        }}
        className="text-[11px] text-civix-cyan hover:underline"
      >
        Recording ↗
      </button>
    );
  }
  return <audio controls preload="none" src={url} className="w-full mt-2" />;
}

// Renders the stored CallSummary (tldr + key points + sentiment). Nothing when
// no summary exists for the call.
function CallSummaryBlock({ summary }) {
  if (!summary) return null;
  return (
    <div className="mt-2 rounded-lg bg-black/20 p-2">
      <div className="flex items-center gap-1 text-[10px] text-emerald-400 mb-1">
        <Sparkles className="w-3 h-3" /> AI Summary
      </div>
      {summary.tldr && <div className="text-[11px] text-white/80">{summary.tldr}</div>}
      {summary.keyPoints?.length > 0 && (
        <ul className="mt-1 list-disc list-inside text-[11px] text-white/60">
          {summary.keyPoints.slice(0, 5).map((k, i) => <li key={i}>{k}</li>)}
        </ul>
      )}
      {summary.sentiment && (
        <div className="mt-1 text-[10px] text-white/40">Sentiment: {summary.sentiment}</div>
      )}
    </div>
  );
}

export default function CallHistoryTab({ calls = [], leadId }) {
  const { data: summariesData } = useQuery({
    queryKey: ['call-summaries', leadId],
    queryFn: () => apiGet(`/leads/${leadId}/summaries`),
    enabled: !!leadId,
    staleTime: 10_000,
  });
  const summaries = summariesData?.summaries || [];
  const summaryFor = (callId) =>
    summaries.find((s) => String(s.callId) === String(callId)) || null;

  if (!calls.length) return <p className="text-xs text-white/40 p-3">No calls logged yet.</p>;

  return (
    <ul className="space-y-2 p-1 overflow-y-auto">
      {calls.map((c) => {
        const inbound = c.direction === 'inbound';
        const Icon = inbound ? PhoneIncoming : PhoneOutgoing;
        const hasRecording = !!(c.recordingUrl || c.recordingSid);
        const lineNumber = inbound ? c.from : c.to;
        return (
          <li key={c._id} className="flex gap-3 rounded-lg bg-civix-panel border border-civix-border px-3 py-2">
            <Icon className="w-4 h-4 text-civix-purpleSoft shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-xs font-semibold text-white/85">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${inbound ? 'bg-sky-500/20 text-sky-300' : 'bg-civix-purple/25 text-civix-purpleSoft'}`}>
                    {inbound ? 'Inbound' : 'Outbound'}
                  </span>
                  {c.agentName || 'Unknown agent'}
                </span>
                <span className="text-[10px] text-white/40 shrink-0" title={fmtDate(c.createdAt)}>{fmtFromNow(c.createdAt)}</span>
              </div>
              <p className="text-[11px] text-white/60 mt-0.5">
                {[c.status, dur(c.duration), c.disposition].filter(Boolean).join(' · ')}
                {lineNumber ? ` · ${inbound ? 'from' : 'to'} ${fmtPhone(lineNumber)}` : ''}
              </p>
              <p className="text-[10px] text-white/35">{fmtDate(c.createdAt)}</p>
              {hasRecording && <RecordingPlayer callId={c._id} />}
              {c.transcription && (
                <p className="text-[11px] text-white/70 italic mt-2">"{c.transcription.slice(0, 200)}…"</p>
              )}
              <CallSummaryBlock summary={summaryFor(c._id)} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
